import React, { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import TileView from './TileView';
import { tileLabel } from '../engine/type';

// BoardView
// Renderiza el tablero con estética Zodiacus, layout responsivo y efectos premium.
// Incluye: marco animado, partículas sutiles, glow por elemento en cada tile.

const ELEMENTS = {
  fire: {
    label: 'Fuego',
    // 🔥 rojo vibrante (premium)
    glow: 'rgba(255, 59, 48, 0.55)',
    edge: 'rgba(255, 180, 120, 0.22)',
    bg: 'radial-gradient(circle at 30% 25%, rgba(255,59,48,0.22), rgba(0,0,0,0) 62%)',
    chip: 'linear-gradient(135deg, #FF3B30, #FF7A18)',
  },
  air: {
    label: 'Aire',
    // 🌬 verde vivo
    glow: 'rgba(46, 204, 113, 0.48)',
    edge: 'rgba(168, 224, 99, 0.22)',
    bg: 'radial-gradient(circle at 30% 25%, rgba(46,204,113,0.18), rgba(0,0,0,0) 62%)',
    chip: 'linear-gradient(135deg, #2ECC71, #A8E063)',
  },
  water: {
    label: 'Agua',
    // 💧 celeste luminoso
    glow: 'rgba(79, 195, 247, 0.55)',
    edge: 'rgba(129, 212, 250, 0.20)',
    bg: 'radial-gradient(circle at 30% 25%, rgba(79,195,247,0.20), rgba(0,0,0,0) 62%)',
    chip: 'linear-gradient(135deg, #4FC3F7, #81D4FA)',
  },
  earth: {
    label: 'Tierra',
    // 🌱 marrón premium
    glow: 'rgba(141, 110, 99, 0.55)',
    edge: 'rgba(218, 165, 32, 0.18)',
    bg: 'radial-gradient(circle at 30% 25%, rgba(141,110,99,0.18), rgba(0,0,0,0) 62%)',
    chip: 'linear-gradient(135deg, #8D6E63, #6D4C41)',
  },
  cosmic: {
    label: 'Cósmico',
    glow: 'rgba(255, 215, 0, 0.35)',
    edge: 'rgba(255,215,0,0.16)',
    bg: 'radial-gradient(circle at 30% 25%, rgba(255,215,0,0.14), rgba(0,0,0,0) 62%)',
    chip: 'linear-gradient(135deg, #FFD700, #FFB700)',
  },
};

function playElementSfx(ctx, elementKey) {
  if (!ctx) return;
  // Mark that we want audio only while playing an SFX
  ctx.__zcWantAudio = true;

  // Hard guard: stop any previously running nodes
  if (ctx.__zcActiveNodes) {
    ctx.__zcActiveNodes.forEach((n) => {
      try { n.stop && n.stop(); } catch (e) {}
      try { n.disconnect && n.disconnect(); } catch (e) {}
    });
    ctx.__zcActiveNodes = null;
  }

  // Guard: only run audio for the brief SFX window
  try {
    if (ctx.state === 'suspended') ctx.resume();
  } catch (e) {}
  if (ctx.state && ctx.state !== 'running') {
    // If we can't run, don't leave the output open
    ctx.__zcWantAudio = false;
    return;
  }

  const now = ctx.currentTime;
  const el = elementKey || 'cosmic';

  // Keep it short and safe: always stop + disconnect everything.
  const stopAt = now + 0.14;

  let gain;
  let o1;
  let o2;
  let lfo;
  let lfoGain;

  const cleanup = () => {
    // Disconnecting prevents any accidental sustained output + helps GC.
    try { if (lfo) lfo.disconnect(); } catch (e) {}
    try { if (lfoGain) lfoGain.disconnect(); } catch (e) {}
    try { if (o1) o1.disconnect(); } catch (e) {}
    try { if (o2) o2.disconnect(); } catch (e) {}
    try { if (gain) gain.disconnect(); } catch (e) {}
    if (ctx.__zcActiveNodes) ctx.__zcActiveNodes = null;
    // Release the audio device shortly after the one-shot ends (prevents continuous buzz/hiss)
    try {
      setTimeout(() => {
        try {
          ctx.__zcWantAudio = false;
          // Do NOT close the AudioContext on each SFX.
          // Closing/recreating frequently can hit iOS limits and break audio.
          // Suspend releases output without destroying the context.
          if (typeof ctx.suspend === 'function' && ctx.state === 'running') {
            ctx.suspend();
          }
        } catch (e) {
          ctx.__zcWantAudio = false;
        }
      }, 40);
    } catch (e) {
      ctx.__zcWantAudio = false;
    }
  };

  try {
    // One-shot, ultra-light synth (no assets)
    gain = ctx.createGain();
    // Slightly lower gain to avoid harshness on headphones
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.09, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);

    o1 = ctx.createOscillator();
    o2 = ctx.createOscillator();

    // Element tuning: distinct + premium
    if (el === 'fire') {
      o1.type = 'sawtooth';
      o2.type = 'triangle';
      o1.frequency.setValueAtTime(520, now);
      o2.frequency.setValueAtTime(260, now);
      o1.frequency.exponentialRampToValueAtTime(680, now + 0.08);
    } else if (el === 'water') {
      o1.type = 'sine';
      o2.type = 'sine';
      o1.frequency.setValueAtTime(420, now);
      o2.frequency.setValueAtTime(210, now);
      o1.frequency.exponentialRampToValueAtTime(320, now + 0.10);
    } else if (el === 'earth') {
      o1.type = 'triangle';
      o2.type = 'triangle';
      o1.frequency.setValueAtTime(220, now);
      o2.frequency.setValueAtTime(110, now);
      o1.frequency.exponentialRampToValueAtTime(160, now + 0.10);
    } else if (el === 'air') {
      o1.type = 'square';
      o2.type = 'sine';
      o1.frequency.setValueAtTime(740, now);
      o2.frequency.setValueAtTime(370, now);
      o1.frequency.exponentialRampToValueAtTime(880, now + 0.06);
    } else {
      // cosmic
      o1.type = 'sine';
      o2.type = 'triangle';
      o1.frequency.setValueAtTime(600, now);
      o2.frequency.setValueAtTime(300, now);
    }

    // Subtle shimmer (very light). Keep modulation small to avoid audible "motor" feel.
    lfo = ctx.createOscillator();
    lfoGain = ctx.createGain();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(8, now);
    lfoGain.gain.setValueAtTime(10, now);
    lfo.connect(lfoGain);
    lfoGain.connect(o1.frequency);

    // Register all nodes for hard stop
    ctx.__zcActiveNodes = [gain, o1, o2, lfo, lfoGain];

    // Routing
    o1.connect(gain);
    o2.connect(gain);
    gain.connect(ctx.destination);

    // Ensure cleanup happens even if the browser misbehaves.
    o1.onended = cleanup;

    o1.start(now);
    o2.start(now);
    lfo.start(now);

    // Hard stop. Some browsers can occasionally ignore very short ramps; explicit stop helps.
    try { o1.stop(stopAt); } catch (e) {}
    try { o2.stop(stopAt); } catch (e) {}
    try { lfo.stop(stopAt); } catch (e) {}

    // Fallback cleanup a bit later (covers cases where onended doesn't fire).
    setTimeout(cleanup, 260);
  } catch (e) {
    // If anything throws, make sure we don't leave running nodes.
    try { if (o1) o1.stop(); } catch (err) {}
    try { if (o2) o2.stop(); } catch (err) {}
    try { if (lfo) lfo.stop(); } catch (err) {}
    cleanup();
  }
}

function normalizeKey(v) {
  if (v == null) return '';
  return String(v)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function guessElement(tile) {
  // Intentamos deducir el “signo”/tipo desde distintas keys sin depender del engine.
  const raw =
    tile?.sign ??
    tile?.zodiacSign ??
    tile?.zodiac ??
    tile?.zodiacName ??
    tile?.glyph ??
    tile?.emoji ??
    tile?.icon ??
    tile?.type ??
    tile?.kind ??
    tile?.id ??
    tile?.key ??
    tile?.name ??
    tile?.value;

  const k = normalizeKey(raw);
  if (!k) return 'cosmic';

  // Zodiac glyphs (most common in UI): map directly to elements
  // ♈ ♉ ♊ ♋ ♌ ♍ ♎ ♏ ♐ ♑ ♒ ♓
  const glyph = String(raw).trim();
  if (glyph === '♈' || glyph === '♌' || glyph === '♐') return 'fire';
  if (glyph === '♉' || glyph === '♍' || glyph === '♑') return 'earth';
  if (glyph === '♊' || glyph === '♎' || glyph === '♒') return 'air';
  if (glyph === '♋' || glyph === '♏' || glyph === '♓') return 'water';

  // Español / Inglés (por si en algún lado cambiaste labels)
  const fire = ['aries', 'leo', 'sagitario', 'sagittarius'];
  const earth = ['tauro', 'taurus', 'virgo', 'capricornio', 'capricorn'];
  const air = ['geminis', 'gemini', 'libra', 'acuario', 'aquarius'];
  const water = ['cancer', 'cáncer', 'escorpio', 'scorpio', 'piscis', 'pisces'];

  if (fire.some((s) => k.includes(s))) return 'fire';
  if (earth.some((s) => k.includes(s))) return 'earth';
  if (air.some((s) => k.includes(s))) return 'air';
  if (water.some((s) => k.includes(s))) return 'water';

  return 'cosmic';
}

function elementFromLabel(label) {
  const g = String(label || '').trim();
  // ♈ ♉ ♊ ♋ ♌ ♍ ♎ ♏ ♐ ♑ ♒ ♓
  if (g === '♈' || g === '♌' || g === '♐') return 'fire';
  if (g === '♉' || g === '♍' || g === '♑') return 'earth';
  if (g === '♊' || g === '♎' || g === '♒') return 'air';
  if (g === '♋' || g === '♏' || g === '♓') return 'water';
  return '';
}

function resolveElement(tile) {
  const guessed = guessElement(tile);
  if (guessed && guessed !== 'cosmic') return guessed;
  const lbl = tileLabel(tile);
  const inferred = elementFromLabel(lbl);
  return inferred || 'cosmic';
}


export default function BoardView({
  board,
  selected,
  onCellClick,
  disabled = false,
  clearingIds = null,
  hintCells = null,
  activatedFx = null,
  fxTick = 0,
  hasHint = false,
  swapAnimation = null,
  presentationMotion = null,
  matchTier = 3,
  specialFx = null,
  worldTint = null,
}) {
  const rows = board?.rows ?? 0;
  const cols = board?.cols ?? 0;
  const grid = useMemo(() => board?.grid ?? [], [board?.grid]);

  // =============================
  // Device hint (for responsive sizing)
  // =============================
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);

  const [vw, setVw] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 999
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let raf = 0;
    const vv = window.visualViewport;

    const readWidth = () => {
      // Prefer visualViewport width on mobile Safari (accounts for address bar / dynamic viewport)
      const w = (vv && typeof vv.width === 'number') ? vv.width : window.innerWidth;
      setVw(Math.round(w));
    };

    const onResize = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        readWidth();
      });
    };

    // Initial sync
    readWidth();

    window.addEventListener('resize', onResize);
    // iOS Safari: visualViewport resize fires when the URL bar collapses/expands
    if (vv && typeof vv.addEventListener === 'function') {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }

    return () => {
      try { window.removeEventListener('resize', onResize); } catch (e) {}
      if (vv && typeof vv.removeEventListener === 'function') {
        try { vv.removeEventListener('resize', onResize); } catch (e) {}
        try { vv.removeEventListener('scroll', onResize); } catch (e) {}
      }
      if (raf) {
        try { window.cancelAnimationFrame(raf); } catch (e) {}
        raf = 0;
      }
    };
  }, []);

  const isMobile = vw <= 520 || isIOS;
  // Charge UI sits ABOVE the square board. Keep it compact to avoid big gaps on mobile.
  // Latch: once we detect charge support, keep the UI space stable to avoid layout jumps ("rebote")
  const [supportsCharge, setSupportsCharge] = useState(false);

  useEffect(() => {
    if (supportsCharge) return;
    const has = board?.meta?.charge != null || board?.meta?.chargeTarget != null;
    if (has) setSupportsCharge(true);
  }, [supportsCharge, board?.meta?.charge, board?.meta?.chargeTarget]);


  // =============================
  // Charge UI (optional)
  // Engine can expose a charge counter via board.meta.charge
  // Supported shapes:
  //  - number (current)
  //  - { current, target }
  //  - { value, max }
  // Also supports board.meta.chargeTarget
  // =============================
  const chargeMeta = board?.meta?.charge ?? null;
  const chargeTarget = useMemo(() => {
    const raw = board?.meta?.chargeTarget;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 3;
  }, [board?.meta?.chargeTarget]);

  const charge = useMemo(() => {
    if (chargeMeta == null) return null;

    // number
    if (typeof chargeMeta === 'number' && Number.isFinite(chargeMeta)) {
      return { current: Math.max(0, Math.floor(chargeMeta)), target: chargeTarget };
    }

    // object shapes
    if (typeof chargeMeta === 'object') {
      const curRaw = chargeMeta.current ?? chargeMeta.value ?? chargeMeta.count ?? null;
      const tgtRaw = chargeMeta.target ?? chargeMeta.max ?? chargeMeta.limit ?? chargeTarget;
      const current = Number(curRaw);
      const target = Number(tgtRaw);
      if (Number.isFinite(current) && Number.isFinite(target) && target > 0) {
        return { current: Math.max(0, Math.floor(current)), target: Math.max(1, Math.floor(target)) };
      }
    }

    return null;
  }, [chargeMeta, chargeTarget]);


  // Accept numbers OR numeric strings from engine/UI
  const toFiniteNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // Treat selection as "active" only when it has valid coordinates.
  // (Some parents keep a truthy object even when nothing is selected.)
  const selR = toFiniteNumber(selected?.r);
  const selC = toFiniteNumber(selected?.c);
  const hasSelection = !!selected && selR != null && selC != null;

  // PERF: cache element resolution (tileLabel/guessing is expensive) so we don't recompute on every render.
  const elementKeyGrid = useMemo(() => {
    return (grid || []).map((row) => (row || []).map((t) => resolveElement(t)));
  }, [grid]);

  // PERF: map tile id -> element for fast cascade dominant-element detection.
  const idToElement = useMemo(() => {
    const m = new Map();
    for (let r = 0; r < (grid || []).length; r++) {
      const row = grid[r] || [];
      const ekRow = elementKeyGrid[r] || [];
      for (let c = 0; c < row.length; c++) {
        const t = row[c];
        const id = t?._id || t?.id;
        if (!id) continue;
        m.set(id, ekRow[c] || 'cosmic');
      }
    }
    return m;
  }, [grid, elementKeyGrid]);

  const cellW = cols > 0 ? 100 / cols : 0;
  const cellH = rows > 0 ? 100 / rows : 0;

  const fxList = useMemo(() => {
    return Array.isArray(activatedFx) ? activatedFx : [];
  }, [activatedFx]);

  // =============================
  // Charge trigger toast (Candy-style feedback)
  // Engine should push activatedFx items with `cause: 'charge_trigger'`.
  // =============================
  const [chargeToast, setChargeToast] = useState(null);
  const toastTimerRef = useRef(null);

  const _toastForFx = useCallback((fx) => {
    const sp = String(fx?.special || '').toLowerCase();
    if (sp.includes('stripe')) return { icon: '⚡', text: 'Rayo activado' };
    if (sp.includes('bomb')) return { icon: '💣', text: 'Bomba activada' };
    if (sp.includes('star')) return { icon: '✨', text: 'Estrella activada' };
    return { icon: '✨', text: 'Power activado' };
  }, []);

  useEffect(() => {
    if (!fxList || fxList.length === 0) return;
    // Take the latest charge-triggered activation in this tick
    const last = [...fxList].reverse().find((fx) => fx?.cause === 'charge_trigger');
    if (!last) return;

    const t = _toastForFx(last);
    setChargeToast(t);

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setChargeToast(null), 900);

    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [fxTick, fxList, _toastForFx]);

  const audioCtxRef = useRef(null);
  const audioPrimedRef = useRef(false);
  const lastDominantRef = useRef('');

  useEffect(() => {
    return () => {
      const ctx = audioCtxRef.current;
      try {
        if (ctx && ctx.__zcActiveNodes) {
          ctx.__zcActiveNodes.forEach((n) => {
            try { n.stop && n.stop(); } catch (e) {}
            try { n.disconnect && n.disconnect(); } catch (e) {}
          });
          ctx.__zcActiveNodes = null;
        }
        if (ctx) ctx.__zcWantAudio = false;
        if (ctx && typeof ctx.close === 'function') ctx.close();
      } catch (e) {}
      audioCtxRef.current = null;
      audioPrimedRef.current = false;
      lastDominantRef.current = '';
    };
  }, []);


  const primeAudio = () => {
  if (audioPrimedRef.current) return;
  audioPrimedRef.current = true;

  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;

    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new Ctx();
      audioCtxRef.current.__zcWantAudio = false;
      audioCtxRef.current.__zcActiveNodes = null;
    }
  } catch (e) {}
};

  const [autoHint, setAutoHint] = useState(null);

  // Single hint point to render (prefer engine-provided hintCells; otherwise use autoHint)
  const hintPoint = useMemo(() => {
    const p = (Array.isArray(hintCells) && hintCells.length > 0) ? hintCells[0] : autoHint;
    if (!p) return null;
    const r = toFiniteNumber(p?.r);
    const c = toFiniteNumber(p?.c);
    if (r == null || c == null) return null;
    return { r, c };
  }, [hintCells, autoHint]);

  const hintPair = useMemo(() => {
    if (!Array.isArray(hintCells) || hintCells.length < 2) return null;
    const a = hintCells[0];
    const b = hintCells[1];
    if (![a, b].every((p) => Number.isFinite(Number(p?.r)) && Number.isFinite(Number(p?.c)))) return null;
    const ar = Number(a.r); const ac = Number(a.c);
    const br = Number(b.r); const bc = Number(b.c);
    const horizontal = ar === br;
    if (!horizontal && ac !== bc) return null;
    const amount = 4;
    return {
      a: { r: ar, c: ac, x: horizontal ? (bc > ac ? amount : -amount) : 0, y: horizontal ? 0 : (br > ar ? amount : -amount) },
      b: { r: br, c: bc, x: horizontal ? (bc > ac ? -amount : amount) : 0, y: horizontal ? 0 : (br > ar ? -amount : amount) },
    };
  }, [hintCells]);

  // 🔥 HINT booster: compute a valid move.
  // - Immediate on activation.
  // - Re-compute after a short idle when the board changes.
  // This avoids running the expensive O(n^2) search twice per update.
  useEffect(() => {
    if (!hasHint) {
      setAutoHint(null);
      return;
    }
    if (disabled || hasSelection || !board || !Array.isArray(board.grid)) return;

    let alive = true;
    const delayMs = autoHint ? 900 : 0; // first time: immediate; later: idle re-hint

    const grid0 = board.grid;
    const rows0 = board.rows || grid0.length;
    const cols0 = board.cols || (grid0[0]?.length || 0);

    const findHintMove = () => {
      const trySwapCreatesMatch = (r1, c1, r2, c2) => {
        const copy = grid0.map(row => row.slice());
        const tmp = copy[r1][c1];
        copy[r1][c1] = copy[r2][c2];
        copy[r2][c2] = tmp;

        const ekGrid = copy.map(row => row.map(t => resolveElement(t)));

        // horizontal
        for (let r = 0; r < rows0; r++) {
          let cnt = 1;
          for (let c = 1; c < cols0; c++) {
            cnt = ekGrid[r][c] === ekGrid[r][c - 1] ? cnt + 1 : 1;
            if (cnt >= 3) return true;
          }
        }

        // vertical
        for (let c = 0; c < cols0; c++) {
          let cnt = 1;
          for (let r = 1; r < rows0; r++) {
            cnt = ekGrid[r][c] === ekGrid[r - 1][c] ? cnt + 1 : 1;
            if (cnt >= 3) return true;
          }
        }

        return false;
      };

      let found = null;

      outer:
      for (let r = 0; r < rows0; r++) {
        for (let c = 0; c < cols0; c++) {
          if (c + 1 < cols0 && trySwapCreatesMatch(r, c, r, c + 1)) {
            found = { r, c };
            break outer;
          }
          if (r + 1 < rows0 && trySwapCreatesMatch(r, c, r + 1, c)) {
            found = { r, c };
            break outer;
          }
        }
      }

      return found;
    };

    const t = setTimeout(() => {
      if (!alive) return;
      const found = findHintMove();
      if (!alive) return;
      setAutoHint(found);
    }, delayMs);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [hasHint, board, disabled, hasSelection, autoHint]);


  const dominantCascadeElement = useMemo(() => {
    if (!clearingIds || typeof clearingIds?.has !== 'function') return '';
    const counts = { fire: 0, air: 0, water: 0, earth: 0, cosmic: 0 };

    // Iterate only the clearing ids (typically small) instead of scanning the whole grid.
    for (const id of clearingIds) {
      const ek = idToElement.get(id) || 'cosmic';
      counts[ek] = (counts[ek] || 0) + 1;
    }

    let best = '';
    let bestN = 0;
    for (const k of Object.keys(counts)) {
      if (counts[k] > bestN) {
        bestN = counts[k];
        best = k;
      }
    }
    return bestN > 0 ? best : '';
  }, [clearingIds, idToElement]);


  // PERF: be conservative with expensive paints/filters on slower devices.
  const cores = typeof navigator !== 'undefined' ? Number(navigator.hardwareConcurrency || 0) : 0;
  const lowEnd = isMobile || (cores > 0 && cores <= 4);

  const fxLow = lowEnd;
  // Sparkles are pretty but heavy; keep them minimal.
  const sparkleCount = fxLow ? 2 : 4;

  const wrapperStyle = useMemo(() => {
    return {
      width: isMobile ? 'calc(100vw - 12px)' : 'min(96vw, 540px)',
      maxWidth: isMobile ? 'unset' : undefined,
      margin: isMobile ? '0 auto' : undefined,
      position: 'relative',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      // No more charge bar paddingTop; charge UI is now in normal flow.
    };
  }, [isMobile]);
  // Ensure UI always has a stable object when we are reserving the bar space.
  const chargeForUi = useMemo(() => {
    if (!supportsCharge) return null;
    // Even if engine temporarily sets charge to null after trigger, keep UI alive at 0/target.
    return charge || { current: 0, target: chargeTarget };
  }, [supportsCharge, charge, chargeTarget]);

  // Always show the bar when the engine supports charge, even at 0/3.
  // This prevents the empty “gap” after triggering and avoids perceived layout bouncing.
  // 🔥 CLAVE: mantener montada la UI aunque `disabled` cambie durante resolve
// así evitamos el “rebote” vertical al mover fichas en mobile.
const showChargeUi = supportsCharge && !!chargeForUi;

  const boardStyle = useMemo(() => {
    return {
      width: '100%',
      aspectRatio: '1 / 1',
      borderRadius: isMobile ? 18 : 22,
      padding: isMobile ? 1 : 8,
      position: 'relative',
      overflow: 'hidden',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'transparent',
      userSelect: 'none',

      // Base cosmic background
      background:
        'radial-gradient(circle at 22% 18%, rgba(255,215,0,0.10), transparent 44%), ' +
        'radial-gradient(circle at 80% 26%, rgba(75,0,130,0.42), transparent 56%), ' +
        'radial-gradient(circle at 40% 80%, rgba(88,255,168,0.08), transparent 50%), ' +
        'linear-gradient(135deg, rgba(75,0,130,0.58), rgba(18,0,40,0.62))',

      border: '1px solid rgba(218,165,32,0.18)',
      boxShadow: isMobile
        ? '0 18px 40px rgba(0,0,0,0.44), 0 0 0 1px rgba(218,165,32,0.18) inset, 0 0 28px rgba(75,0,130,0.34)'
        : '0 18px 42px rgba(0,0,0,0.42)',
      backdropFilter: isMobile ? 'none' : 'blur(10px)',
      WebkitBackdropFilter: isMobile ? 'none' : 'blur(10px)',
      transform: 'translateZ(0)',
    };
  }, [isMobile]);

  const innerGridStyle = useMemo(() => {
    const isTiny = vw < 360;
    const gap = isMobile ? (isTiny ? 0.75 : 1) : (isTiny ? 3 : 4);

    return {
      position: 'relative',
      zIndex: 5,
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gridTemplateRows: `repeat(${rows}, 1fr)`,
      gap,
      width: '100%',
      height: '100%',
      padding: 0,
      touchAction: 'manipulation',
    };
  }, [rows, cols, isMobile, vw]);

  const css = useMemo(
    () => `
      @keyframes zc-rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
        

      @keyframes zc-float {
        0% { transform: translate3d(0,0,0); opacity: 0.55; }
        50% { transform: translate3d(0,-8px,0); opacity: 0.75; }
        100% { transform: translate3d(0,0,0); opacity: 0.55; }
      }
      @keyframes zc-twinkle {
        0% { opacity: 0.25; }
        50% { opacity: 0.55; }
        100% { opacity: 0.25; }
      }
      @keyframes zc-pulse {
        0% { transform: scale(1); filter: brightness(1); }
        50% { transform: scale(1.02); filter: brightness(1.06); }
        100% { transform: scale(1); filter: brightness(1); }
      }
      @keyframes zc-hint-swap {
        0%, 42%, 100% { transform: translate3d(0, 0, 0); filter: brightness(1); }
        12% { transform: translate3d(var(--zc-hint-x, 0px), var(--zc-hint-y, 0px), 0); filter: brightness(1.06); }
        24% { transform: translate3d(0, 0, 0); filter: brightness(1); }
      }
      @keyframes zc-hint-highlight {
        0%, 42%, 100% { filter: brightness(1); }
        12%, 24% { filter: brightness(1.07); }
      }
      @keyframes zc-toast-in {
        0% { transform: translate3d(-50%, -8px, 0) scale(0.98); opacity: 0; }
        25% { opacity: 1; }
        100% { transform: translate3d(-50%, 0px, 0) scale(1); opacity: 1; }
      }
      @keyframes zc-toast-out {
        0% { transform: translate3d(-50%, 0px, 0) scale(1); opacity: 1; }
        100% { transform: translate3d(-50%, -8px, 0) scale(0.98); opacity: 0; }
      }
      @keyframes zc-clear-pop {
        0% { transform: scale(1); filter: brightness(1); opacity: 1; }
        50% { transform: scale(1.08); filter: brightness(1.35) saturate(1.25); opacity: 0.95; }
        100% { transform: scale(0.92); filter: brightness(0.9) saturate(0.9); opacity: 0.0; }
      }
      @keyframes zc-clear-pop-lite {
        0% { transform: scale(1); opacity: 1; }
        60% { transform: scale(1.06); opacity: 0.85; }
        100% { transform: scale(0.94); opacity: 0.0; }
      }
      @keyframes zc-swap-travel {
        from { transform: translate3d(var(--zc-swap-x, 0), var(--zc-swap-y, 0), 0); }
        to { transform: translate3d(0, 0, 0); }
      }
      @keyframes zc-tile-fall {
        from { transform: translate3d(0, var(--zc-motion-y, 0), 0); opacity: 0.96; }
        to { transform: translate3d(0, 0, 0); opacity: 1; }
      }
      @keyframes zc-tile-spawn {
        from { transform: translate3d(0, var(--zc-motion-y, -100%), 0); opacity: 0.35; }
        to { transform: translate3d(0, 0, 0); opacity: 1; }
      }
      @keyframes zc-match-cue {
        0% { transform: scale(0.72); opacity: 0; }
        25% { opacity: 0.95; }
        100% { transform: scale(1.08); opacity: 0; }
      }
      @keyframes zc-special-create-star {
        0% { transform: translate3d(-50%, -50%, 0) scale(.55) rotate(-10deg); opacity: 0; }
        24% { opacity: 1; }
        72% { transform: translate3d(-50%, -50%, 0) scale(1.08) rotate(4deg); opacity: .92; }
        100% { transform: translate3d(-50%, -50%, 0) scale(1) rotate(0); opacity: 0; }
      }
      @keyframes zc-special-create-stripe {
        0% { transform: translate3d(-50%, -50%, 0) scaleX(.2); opacity: 0; }
        28% { opacity: .95; }
        100% { transform: translate3d(-50%, -50%, 0) scaleX(1); opacity: 0; }
      }
      @keyframes zc-special-create-bomb {
        0% { transform: translate3d(-50%, -50%, 0) scale(.65); opacity: 0; }
        28% { opacity: .9; }
        100% { transform: translate3d(-50%, -50%, 0) scale(1.12); opacity: 0; }
      }
      @keyframes zc-special-hit {
        0% { transform: translate3d(-50%, -50%, 0) scale(.55); opacity: 0; }
        18% { opacity: .88; }
        100% { transform: translate3d(-50%, -50%, 0) scale(1.18); opacity: 0; }
      }
      @keyframes zc-special-combo {
        0% { opacity: 0; transform: scale(.72); }
        24% { opacity: .9; transform: scale(1); }
        100% { opacity: 0; transform: scale(1.18); }
      }
      @media (prefers-reduced-motion: reduce) {
        .zc-hint-motion {
          animation-name: zc-hint-highlight !important;
          animation-duration: 900ms !important;
          animation-iteration-count: 1 !important;
        }
        .zc-special-create-star,
        .zc-special-create-stripe,
        .zc-special-create-bomb,
        .zc-special-hit,
        .zc-special-combo {
          animation-duration: 1ms !important;
          animation-delay: 0ms !important;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .zc-swap-travel,
        .zc-tile-fall,
        .zc-tile-spawn,
        .zc-match-creation-cue {
          animation-duration: 1ms !important;
          animation-delay: 0ms !important;
        }
      }
      @keyframes zc-sparkle {
        0% { transform: translate3d(0,0,0) scale(0.6); opacity: 0; }
        20% { opacity: 1; }
        100% { transform: translate3d(var(--dx), var(--dy), 0) scale(1.15); opacity: 0; }
      }
      @keyframes zc-beam-h {
        0% { transform: translate3d(-8%, 0, 0) scaleX(0.85); opacity: 0; }
        12% { opacity: 1; }
        55% { opacity: 0.95; }
        100% { transform: translate3d(8%, 0, 0) scaleX(1.02); opacity: 0; }
      }
      @keyframes zc-beam-v {
        0% { transform: translate3d(0, -8%, 0) scaleY(0.85); opacity: 0; }
        12% { opacity: 1; }
        55% { opacity: 0.95; }
        100% { transform: translate3d(0, 8%, 0) scaleY(1.02); opacity: 0; }
      }
      @keyframes zc-star-burst {
        0% { transform: scale(0.55) rotate(0deg); opacity: 0; }
        15% { opacity: 1; }
        100% { transform: scale(1.28) rotate(18deg); opacity: 0; }
      }
      @keyframes zc-shockwave {
        0% { transform: scale(0.55); opacity: 0.0; }
        18% { opacity: 0.65; }
        100% { transform: scale(1.8); opacity: 0; }
      }
      @keyframes zc-tile-idle {
  0%   { transform: translate3d(0,0,0) scale(1); }
  50%  { transform: translate3d(0,-0.6px,0) scale(1.012); }
  100% { transform: translate3d(0,0,0) scale(1); }
}
      @keyframes zc-sheen {
        0% { transform: translate3d(-120%, 0, 0) rotate(18deg); opacity: 0; }
        12% { opacity: 0.18; }
        55% { opacity: 0.16; }
        100% { transform: translate3d(120%, 0, 0) rotate(18deg); opacity: 0; }
      }
      @keyframes zc-elem-shock {
        0% { transform: translate3d(-50%, -50%, 0) scale(0.6); opacity: 0; }
        18% { opacity: 0.65; }
        100% { transform: translate3d(-50%, -50%, 0) scale(1.85); opacity: 0; }
      }
      @keyframes zc-aura {
        0% { transform: scale(1); opacity: 0.45; }
        50% { transform: scale(1.06); opacity: 0.65; }
        100% { transform: scale(1); opacity: 0.45; }
      }
      @keyframes zc-board-tint {
        0% { opacity: 0.0; transform: scale(0.98); }
        20% { opacity: 0.55; transform: scale(1.02); }
        100% { opacity: 0.0; transform: scale(1.08); }
      }
      /* Mobile perf: disable ONLY continuous/idle animations (keep clear/FX animations) */
      .zc-mobile .zcBoardFrame,
      .zc-mobile .zcBoardVignette,
      .zc-mobile .zcBoardGlow,
      .zc-mobile .zcBoardStars{
        animation: none !important;
        filter: none !important;
        opacity: 0.45 !important;
      }

      /* Keep tiles crisp on mobile: remove continuous idle sheen/float only */
      .zc-mobile .zcTileSheen,
      .zc-mobile .zcTileAura,
      .zc-mobile .zcTileSelectedGlow{
        animation: none !important;
      }

      /* IMPORTANT: do NOT globally disable animations on mobile.
         We want to keep zc-clear-pop / beams / star burst for premium feedback. */
      /* Make tiles bigger + premium on mobile (emoji readable) */
      .zc-mobile .zcTileBtn{
        font-size: 28px !important;
        border-radius: 18px !important;
      }
      .zc-mobile .zcTileBtn:active{
        transform: translateY(0px) scale(0.97) !important;
      }
      .zc-mobile .zcTileBtn span{
        filter: drop-shadow(0 10px 18px rgba(0,0,0,0.55));
      }

      /* Slightly thicker tile chrome on mobile for a premium feel */
      .zc-mobile .zcTileBtn{
        box-shadow: 0 16px 28px rgba(0,0,0,0.48), inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -12px 20px rgba(0,0,0,0.30) !important;
      }
    `,
    []
  );
  useEffect(() => {
  const id = 'zc-board-css';
  if (document.getElementById(id)) return;

  const style = document.createElement('style');
  style.id = id;
  style.innerHTML = css;
  document.head.appendChild(style);
}, [css]);

  useEffect(() => {
    if (!dominantCascadeElement) return;
    // Never play SFX until the user has interacted (prevents audio starting on room enter)
    if (!audioPrimedRef.current) return;
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // Only play when dominant changes (avoid spam)
    if (lastDominantRef.current === dominantCascadeElement) return;
    lastDominantRef.current = dominantCascadeElement;
    try {
      playElementSfx(ctx, dominantCascadeElement);
    } catch (e) {}
  }, [dominantCascadeElement]);

  const handleCellTap = useCallback(
  (r, c) => {
    if (disabled) return;

    if (!hasHint) {
      setAutoHint(null);
    }

    primeAudio();

    onCellClick?.({ r, c });
  },
  [disabled, onCellClick, hasHint]
);

  if (!board) return null;

  return (
    <div
      style={wrapperStyle}
      className={isMobile ? 'zc-mobile' : ''}
      aria-label="Zodiac Crush board"
    >
     

      
      {/* ⚡ CARGA CÓSMICA (Candy-style, siempre estable para evitar “rebote”) */}
      {showChargeUi && (
        <div
          style={{
            width: '100%',
            padding: isMobile ? '6px 8px' : '7px 12px',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.38)',
            border: '1px solid rgba(218,165,32,0.20)',
            boxShadow:
              '0 10px 18px rgba(0,0,0,0.28), 0 0 18px rgba(255,215,0,0.10)',
            color: 'rgba(255,255,255,0.92)',
            fontSize: isMobile ? 11 : 12,
            lineHeight: 1.15,
            letterSpacing: 0.2,
            textShadow: '0 10px 18px rgba(0,0,0,0.55)',
            backdropFilter: isMobile ? 'none' : 'blur(10px)',
            WebkitBackdropFilter: isMobile ? 'none' : 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? 8 : 10,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {/* Left: label + counter */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              flex: '0 0 auto',
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontSize: isMobile ? 14 : 15,
                filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.55))',
                fontFamily:
                  'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif',
              }}
              aria-hidden="true"
            >
              🔋
            </span>
            <span style={{ fontWeight: 900, opacity: 0.96 }}>
              Carga {chargeForUi.current}/{chargeForUi.target}
            </span>
          </div>

          {/* Middle: mini progress bar */}
          <div
            style={{
              flex: '1 1 auto',
              height: isMobile ? 9 : 10,
              borderRadius: 999,
              background: 'rgba(0,0,0,0.26)',
              border: '1px solid rgba(255,255,255,0.08)',
              overflow: 'hidden',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
              minWidth: 60,
            }}
            aria-hidden="true"
          >
            <div
              style={{
                width: `${Math.min(
                  100,
                  Math.max(0, (chargeForUi.current / chargeForUi.target) * 100)
                )}%`,
                height: '100%',
                borderRadius: 999,
                background:
                  'linear-gradient(90deg, rgba(255,215,0,0.88), rgba(255,183,0,0.72), rgba(75,0,130,0.62))',
                boxShadow: '0 0 16px rgba(255,215,0,0.20)',
                transition: 'width 180ms ease',
              }}
            />
          </div>

          {/* Right: what can happen */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 6 : 8,
              flex: '0 0 auto',
              opacity: 0.92,
            }}
          >
            <span style={{ opacity: 0.55 }}>⇒</span>

            <span
              style={{
                fontSize: isMobile ? 13 : 15,
                filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.55))',
                fontFamily:
                  'Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif',
              }}
              aria-hidden="true"
            >
              ⚡️
            </span>
            <span style={{ opacity: 0.45 }}>·</span>
            <span style={{ fontSize: isMobile ? 13 : 15 }} aria-hidden="true">
              💣
            </span>
            <span style={{ opacity: 0.45 }}>·</span>
            <span style={{ fontSize: isMobile ? 13 : 15 }} aria-hidden="true">
              🌟
            </span>

            <span style={{ opacity: 0.6 }}>(aleatorio)</span>
          </div>
        </div>
      )}

      <div style={{ ...boardStyle, flex: '0 0 auto' }}>
        {/* 🍬 Toast Candy: muestra qué salió cuando se dispara la carga (charge_trigger) */}
        {chargeToast && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              top: isMobile ? 10 : 12,
              transform: 'translate3d(-50%, 0, 0)',
              zIndex: 80,
              pointerEvents: 'none',
              padding: isMobile ? '8px 12px' : '9px 14px',
              borderRadius: 999,
              background: 'rgba(0,0,0,0.42)',
              border: '1px solid rgba(218,165,32,0.22)',
              color: 'rgba(255,255,255,0.96)',
              boxShadow: '0 14px 26px rgba(0,0,0,0.35), 0 0 22px rgba(255,215,0,0.16)',
              backdropFilter: isMobile ? 'none' : 'blur(10px)',
              WebkitBackdropFilter: isMobile ? 'none' : 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              letterSpacing: 0.2,
              animation: 'zc-toast-in 140ms ease-out both',
            }}
          >
            <span style={{ fontSize: isMobile ? 18 : 19, filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.45))' }}>
              {chargeToast.icon}
            </span>
            <span style={{ fontWeight: 800, fontSize: isMobile ? 12 : 13 }}>
              {chargeToast.text}
            </span>
          </div>
        )}

      {/* Marco animado tipo “premium” */}
      <div
        aria-hidden="true"
        className="zcBoardFrame"
        style={{
          position: 'absolute',
          inset: -60,
          background:
            'conic-gradient(from 180deg, rgba(255,215,0,0.00), rgba(255,215,0,0.22), rgba(75,0,130,0.20), rgba(88,255,168,0.12), rgba(255,215,0,0.22), rgba(255,215,0,0.00))',
          filter: 'blur(18px)',
          opacity: 0.7,
          animation: 'zc-rotate 14s linear infinite',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Vignette + depth */}
      <div
        aria-hidden="true"
        className="zcBoardVignette"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 50% 45%, rgba(0,0,0,0.00), rgba(0,0,0,0.35) 72%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />

      {/* Glow sutil arriba */}
      <div
        aria-hidden="true"
        className="zcBoardGlow"
        style={{
          position: 'absolute',
          left: -60,
          top: -60,
          width: 240,
          height: 240,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,215,0,0.18), transparent 70%)',
          filter: 'blur(8px)',
          pointerEvents: 'none',
          zIndex: 3,
          animation: 'zc-float 6.8s ease-in-out infinite',
        }}
      />

      {/* Partículas / estrellas (ultra sutil) */}
      <div
        aria-hidden="true"
        className="zcBoardStars"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 4,
          opacity: 0.5,
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.28) 1px, transparent 1px), radial-gradient(rgba(255,215,0,0.18) 1px, transparent 1px)',
          backgroundSize: '22px 22px, 36px 36px',
          backgroundPosition: '0 0, 10px 16px',
          filter: 'blur(0.2px)',
          animation: 'zc-twinkle 5.4s ease-in-out infinite',
        }}
      />

      {dominantCascadeElement && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: -20,
            pointerEvents: 'none',
            zIndex: 4,
            opacity: 0,
            animation: 'zc-board-tint 520ms ease-out both',
            background:
              `radial-gradient(circle at 50% 45%, ${
                (ELEMENTS[dominantCascadeElement] || ELEMENTS.cosmic).glow
              }, rgba(0,0,0,0) 62%)`,
            filter: 'none',
          }}
        />
      )}

      <div
  style={innerGridStyle}
  onPointerDown={(e) => {
    if (disabled) return;

    const el = e.target.closest('[data-cell]');
    if (!el) return;

    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const r = Number(el.getAttribute('data-r'));
    const c = Number(el.getAttribute('data-c'));

    if (Number.isFinite(r) && Number.isFinite(c)) {
      handleCellTap(r, c);
    }
  }}
>
        {/* FX overlay (stripes / star) */}
        {fxList.length > 0 && rows > 0 && cols > 0 && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              contain: 'layout paint',
              zIndex: 6,
            }}
          >
            {fxList.map((fx, idx) => {
              const r0 = fx?.at?.r;
              const c0 = fx?.at?.c;
              if (!Number.isFinite(r0) || !Number.isFinite(c0)) return null;

              const left = `${(c0 + 0.5) * cellW}%`;
              const top = `${(r0 + 0.5) * cellH}%`;
              const sp = String(fx?.special || '').toLowerCase();
              const isStripeH = fx?.special === 'stripe_h' || fx?.special === 'STRIPE_H' || sp.includes('stripe_h');
              const isStripeV = fx?.special === 'stripe_v' || fx?.special === 'STRIPE_V' || sp.includes('stripe_v');
              const isStar = fx?.special === 'star' || fx?.special === 'STAR' || sp.includes('star');
              const isBomb = fx?.special === 'bomb' || fx?.special === 'BOMB' || sp.includes('bomb');

              const tAt = grid?.[r0]?.[c0];
              const ekAt = resolveElement(tAt);
              const elAt = ELEMENTS[ekAt] || ELEMENTS.cosmic;

              // Beam color: element-dominant (premium)
              const beam = `linear-gradient(90deg, rgba(0,0,0,0), ${elAt.glow} 22%, rgba(255,255,255,0.20) 50%, ${elAt.glow} 72%, rgba(0,0,0,0))`;

              return (
                <React.Fragment key={`fx_${fxTick}_${idx}_${r0}_${c0}`}>
                  {(isStripeH || isStripeV) && (
                    <div
                      style={{
                        position: 'absolute',
                        left: isStripeH ? 0 : `calc(${left} - 6px)`,
                        top: isStripeH ? `calc(${top} - 6px)` : 0,
                        width: isStripeH ? '100%' : 12,
                        height: isStripeH ? 12 : '100%',
                        borderRadius: 999,
                        background: beam,
                        filter: 'none',
                        boxShadow: '0 0 22px rgba(255,215,0,0.25), 0 0 28px rgba(75,0,130,0.18)',
                        opacity: 0,
                        animation: isStripeH ? 'zc-beam-h 260ms ease-out both' : 'zc-beam-v 260ms ease-out both',
                        zIndex: 7,
                      }}
                    />
                  )}

                  {isStar && (
                    <>
                      <div
                        style={{
                          position: 'absolute',
                          left,
                          top,
                          width: 130,
                          height: 130,
                          transform: 'translate3d(-50%, -50%, 0)',
                          borderRadius: '50%',
                          background:
                            `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.60), ${elAt.glow} 38%, rgba(0,0,0,0) 72%)`,
                          filter: 'none',
                          opacity: 0,
                          animation: 'zc-star-burst 360ms ease-out both',
                          boxShadow: '0 0 34px rgba(255,215,0,0.22)',
                          zIndex: 7,
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          left,
                          top,
                          width: 110,
                          height: 110,
                          transform: 'translate3d(-50%, -50%, 0)',
                          borderRadius: '50%',
                          border: `1px solid ${elAt.edge}`,
                          opacity: 0,
                          animation: 'zc-shockwave 380ms ease-out both',
                          boxShadow: '0 0 18px rgba(255,215,0,0.18)',
                          zIndex: 7,
                        }}
                      />
                    </>
                  )}

                  {isBomb && (
                    <>
                      {/* Bomb core flash */}
                      <div
                        style={{
                          position: 'absolute',
                          left,
                          top,
                          width: 140,
                          height: 140,
                          transform: 'translate3d(-50%, -50%, 0)',
                          borderRadius: '50%',
                          background:
                            `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.62), ${elAt.glow} 42%, rgba(0,0,0,0) 74%)`,
                          opacity: 0,
                          animation: 'zc-star-burst 320ms ease-out both',
                          boxShadow: `0 0 36px ${elAt.glow}`,
                          zIndex: 7,
                          pointerEvents: 'none',
                        }}
                      />

                      {/* Bomb shockwave */}
                      <div
                        style={{
                          position: 'absolute',
                          left,
                          top,
                          width: 120,
                          height: 120,
                          transform: 'translate3d(-50%, -50%, 0)',
                          borderRadius: '50%',
                          border: `2px solid ${elAt.edge}`,
                          opacity: 0,
                          animation: 'zc-shockwave 420ms ease-out both',
                          boxShadow: '0 0 18px rgba(255,215,0,0.18)',
                          zIndex: 7,
                          pointerEvents: 'none',
                        }}
                      />

                      {/* Extra ring for bomb identity */}
                      <div
                        style={{
                          position: 'absolute',
                          left,
                          top,
                          width: 156,
                          height: 156,
                          transform: 'translate3d(-50%, -50%, 0)',
                          borderRadius: '50%',
                          border: '1px solid rgba(255,215,0,0.38)',
                          opacity: 0,
                          animation: 'zc-shockwave 520ms ease-out both',
                          boxShadow: '0 0 28px rgba(255,215,0,0.22)',
                          zIndex: 7,
                          pointerEvents: 'none',
                        }}
                      />
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Presentation-only special creation/activation feedback. All
            coordinates and affected cells come from resolver events. */}
        {specialFx && rows > 0 && cols > 0 && (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 18, overflow: 'visible' }}>
            {(Array.isArray(specialFx.clearedCells) ? specialFx.clearedCells : []).slice(0, 12).map((cell, index) => {
              const r0 = Number(cell?.r);
              const c0 = Number(cell?.c);
              if (!Number.isFinite(r0) || !Number.isFinite(c0)) return null;
              const ek = resolveElement(grid?.[r0]?.[c0]);
              const tint = worldTint || (ELEMENTS[ek] || ELEMENTS.cosmic).glow;
              return (
                <div
                  key={`special_hit_${specialFx.key}_${r0}_${c0}_${index}`}
                  className="zc-special-hit"
                  style={{
                    position: 'absolute',
                    left: `${(c0 + 0.5) * cellW}%`,
                    top: `${(r0 + 0.5) * cellH}%`,
                    width: `calc(${cellW}% * .72)`,
                    height: `calc(${cellH}% * .72)`,
                    borderRadius: 14,
                    border: `1px solid ${tint}`,
                    boxShadow: `0 0 16px ${tint}`,
                    background: `radial-gradient(circle, rgba(255,255,255,.34), ${tint} 34%, transparent 72%)`,
                    animationDelay: `${Math.min(36, index * 3)}ms`,
                  }}
                />
              );
            })}

            {(Array.isArray(specialFx.activations) ? specialFx.activations : []).slice(0, 8).map((activation, index) => {
              const r0 = Number(activation?.at?.r);
              const c0 = Number(activation?.at?.c);
              if (!Number.isFinite(r0) || !Number.isFinite(c0)) return null;
              const sp = String(activation?.special || '').toLowerCase();
              const ek = resolveElement(grid?.[r0]?.[c0]);
              const tint = worldTint || (ELEMENTS[ek] || ELEMENTS.cosmic).glow;
              const stripeH = sp.includes('stripe_h');
              const stripeV = sp.includes('stripe_v');
              return (
                <React.Fragment key={`special_activation_${specialFx.key}_${index}`}>
                  {(stripeH || stripeV) && (
                    <div
                      className="zc-special-combo"
                      style={{
                        position: 'absolute',
                        left: stripeH ? 0 : `${(c0 + 0.5) * cellW}%`,
                        top: stripeH ? `${(r0 + 0.5) * cellH}%` : 0,
                        width: stripeH ? '100%' : 10,
                        height: stripeH ? 10 : '100%',
                        transform: stripeH ? 'translateY(-50%)' : 'translateX(-50%)',
                        background: `linear-gradient(${stripeH ? '90deg' : '180deg'}, transparent, ${tint}, rgba(255,255,255,.9), ${tint}, transparent)`,
                        boxShadow: `0 0 20px ${tint}`,
                      }}
                    />
                  )}
                  {(sp.includes('star') || sp.includes('bomb')) && (
                    <div
                      className="zc-special-combo"
                      style={{
                        position: 'absolute',
                        left: `${(c0 + 0.5) * cellW}%`,
                        top: `${(r0 + 0.5) * cellH}%`,
                        width: sp.includes('bomb') ? 118 : 104,
                        height: sp.includes('bomb') ? 118 : 104,
                        transform: 'translate3d(-50%, -50%, 0)',
                        borderRadius: '50%',
                        border: `2px solid ${tint}`,
                        boxShadow: `0 0 26px ${tint}, inset 0 0 18px ${tint}`,
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}

            {specialFx.creation?.at && (
              <div
                className={`zc-special-create-${specialFx.creation.type === 'star' ? 'star' : specialFx.creation.type === 'bomb' ? 'bomb' : 'stripe'}`}
                style={{
                  position: 'absolute',
                  left: `${(Number(specialFx.creation.at.c) + 0.5) * cellW}%`,
                  top: `${(Number(specialFx.creation.at.r) + 0.5) * cellH}%`,
                  width: specialFx.creation.type === 'star' ? 76 : 68,
                  height: specialFx.creation.type === 'star' ? 76 : 68,
                  borderRadius: specialFx.creation.type === 'stripe' ? 12 : '50%',
                  border: '2px solid rgba(255,255,255,.86)',
                  background: 'radial-gradient(circle, rgba(255,255,255,.62), rgba(255,215,0,.24) 38%, transparent 72%)',
                  boxShadow: '0 0 26px rgba(255,215,0,.64)',
                  pointerEvents: 'none',
                }}
              />
            )}

            {specialFx.combo && (
              <div
                className="zc-special-combo"
                style={{ position: 'absolute', inset: 8, borderRadius: 18, border: '1px solid rgba(255,215,0,.74)', boxShadow: '0 0 30px rgba(255,215,0,.34)' }}
              />
            )}
          </div>
        )}

        {grid.map((row, r) =>
          row.map((tile, c) => {
            const isSel = hasSelection && selR === r && selC === c;
            const elementKey = (elementKeyGrid[r] && elementKeyGrid[r][c]) || 'cosmic';
            const el = ELEMENTS[elementKey] || ELEMENTS.cosmic;

            const isClearing = !!clearingIds && typeof clearingIds?.has === 'function' && clearingIds.has(tile?._id || tile?.id);
            // Compose isHint: prefer hintCells when it has entries; otherwise fall back to autoHint
            // IMPORTANT: an empty array `[]` should NOT block autoHint.
            let isHint = false;
            if (Array.isArray(hintCells) && hintCells.length > 0) {
              isHint = hintCells.some((p) => p?.r === r && p?.c === c);
            } else if (hintPoint && hintPoint.r === r && hintPoint.c === c) {
              isHint = true;
            }
            const sp = String(tile?.special || tile?.power || '').toLowerCase();
            const isSpecial = sp.includes('stripe') || sp.includes('star') || sp.includes('bomb');
            const swapPoint = swapAnimation && Array.isArray(swapAnimation.cells)
              ? swapAnimation.cells.find((p) => p?.r === r && p?.c === c)
              : null;
            const motionPoint = presentationMotion?.moves?.find((p) => p?.id && p.id === (tile?._id || tile?.id));
            const motionAnimation = motionPoint
              ? (motionPoint.type === 'spawn' ? 'zc-tile-spawn' : 'zc-tile-fall')
              : null;
            const tileAnimation = swapPoint
              ? 'zc-swap-travel'
              : motionAnimation
                ? motionAnimation
              : (hintPair && !disabled && ((hintPair.a.r === r && hintPair.a.c === c) || (hintPair.b.r === r && hintPair.b.c === c))
                  ? 'zc-hint-motion'
                  : null);
            const hintMotion = hintPair && (hintPair.a.r === r && hintPair.a.c === c
              ? hintPair.a
              : hintPair.b.r === r && hintPair.b.c === c ? hintPair.b : null);
            const matchCue = isClearing && Number(matchTier || 3) >= 4;

            return (
              <div
  key={`${r}_${c}`}
  className={tileAnimation ? `zc-${tileAnimation}` : ''}
  data-cell="1"
  data-r={r}
  data-c={c}
                data-hint={isHint ? '1' : '0'}
                
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  userSelect: 'none',
                  
                  borderRadius: 16,
                  // Fondo por elemento
                  background:
                    `${el.bg}, ${el.chip}`,
                  // Marco sutil
                  border: `1px solid ${el.edge}`,
                  // Glow premium
                  // PERF: during clear, avoid box-shadows (paint heavy). Use overlays instead.
                  boxShadow: isClearing
                    ? 'none'
                    : isSel
                      ? `0 0 0 1px rgba(255,255,255,0.14) inset, 0 0 0 2px rgba(255,215,0,0.18), 0 16px 28px rgba(0,0,0,0.35), 0 0 22px ${el.glow}`
                      : fxLow
                        ? '0 6px 12px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.16) inset'
                        : `0 0 0 1px rgba(0,0,0,0.18) inset, 0 12px 22px rgba(0,0,0,0.28), 0 0 12px rgba(255,215,0,0.06)`,
                  transform: isSel ? 'translateZ(0) scale(1.03)' : 'translateZ(0)',
                  transition: isClearing
                    ? (fxLow ? 'opacity 90ms linear' : 'opacity 120ms ease')
                    : 'transform 120ms ease, box-shadow 140ms ease, filter 140ms ease, opacity 140ms ease',
                  // PERF: avoid filter changes (paint heavy) on match.
                  filter: 'none',
                  opacity: isClearing ? 0.86 : 1,
                  animationName:
                    tileAnimation
                      ? tileAnimation
                      : isClearing
                      ? (fxLow ? 'zc-clear-pop-lite' : 'zc-clear-pop')
                      : (!disabled && !isMobile ? 'zc-tile-idle' : 'none'),
                  animationDuration: swapPoint
                    ? `${swapAnimation.duration || 160}ms`
                    : motionPoint
                      ? `${motionPoint.duration || 180}ms`
                      : tileAnimation === 'zc-hint-motion'
                        ? '3.2s'
                      : (isClearing ? (fxLow ? '180ms' : '220ms') : (!disabled ? '1.9s' : '0s')),
                  animationTimingFunction: tileAnimation ? 'cubic-bezier(.22,.8,.26,1)' : (isClearing ? 'ease-out' : 'ease-in-out'),
                  animationIterationCount: tileAnimation === 'zc-hint-motion'
                    ? 'infinite'
                    : (tileAnimation || isClearing ? '1' : (!disabled && !isMobile ? 'infinite' : '1')),
                  animationFillMode: tileAnimation || isClearing ? 'both' : 'none',
                  animationDirection: 'normal',
                  animationPlayState: 'running',
                  animationDelay: swapPoint
                    ? '0ms'
                    : motionPoint
                      ? `${motionPoint.delay || 0}ms`
                      : (isClearing ? '0s' : `${((r * 7 + c) % 10) * 0.07}s`),
                  willChange: isClearing || isSel ? 'transform, opacity' : 'auto',
                  // PERF: isolate each tile's layout/paint as much as possible.
                  contain: 'layout paint',
                  overflow: 'hidden',
                  '--zc-swap-x': swapPoint?.x || '0%',
                  '--zc-swap-y': swapPoint?.y || '0%',
                  '--zc-motion-y': motionPoint?.y || '0%',
                  '--zc-hint-x': `${hintMotion?.x || 0}px`,
                  '--zc-hint-y': `${hintMotion?.y || 0}px`,
                }}
              >
                {/* Sheen sutil animado */}
                {!isClearing && (
                  <div
                    aria-hidden="true"
                    className="zcTileSheen"
                    style={{
                      position: 'absolute',
                      inset: -20,
                      pointerEvents: 'none',
                      opacity: 0,
                      background:
                        'linear-gradient(90deg, rgba(255,255,255,0.00), rgba(255,255,255,0.12), rgba(255,215,0,0.10), rgba(255,255,255,0.00))',
                      filter: 'blur(0.2px)',
                      // Animation longhand properties (avoid mixing shorthand with animationDelay)
                      animationName: 'zc-sheen',
                      animationDuration: '3.8s',
                      animationTimingFunction: 'ease-in-out',
                      animationIterationCount: 'infinite',
                      animationFillMode: 'none',
                      animationDirection: 'normal',
                      animationPlayState: 'running',
                      animationDelay: `${((r * 11 + c) % 14) * 0.18}s`,
                      mixBlendMode: 'screen',
                      zIndex: 2,
                    }}
                  />
                )}
                {/* Shimmer para tile seleccionado */}
                {isSel && (
                  <div
                    aria-hidden="true"
                    className="zcTileSelectedGlow"
                    style={{
                      position: 'absolute',
                      inset: -60,
                      background:
                        'radial-gradient(circle at 30% 35%, rgba(255,215,0,0.18), transparent 50%), ' +
                        'radial-gradient(circle at 70% 65%, rgba(255,255,255,0.10), transparent 55%)',
                      filter: 'blur(10px)',
                      opacity: 0.95,
                      pointerEvents: 'none',
                      animation: 'zc-pulse 1.35s ease-in-out infinite',
                    }}
                  />
                )}


                {isSpecial && !isClearing && (
                  <div
                    aria-hidden="true"
                    className="zcTileAura"
                    style={{
                      position: 'absolute',
                      inset: 6,
                      borderRadius: 12,
                      border: `1px solid ${el.edge}`,
                      boxShadow: `0 0 0 1px rgba(255,255,255,0.08), 0 0 18px ${el.glow}`,
                      animation: 'zc-aura 1.2s ease-in-out infinite',
                      pointerEvents: 'none',
                      zIndex: 3,
                      mixBlendMode: 'screen',
                    }}
                  />
                )}

                {isClearing && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      zIndex: 10,
                    }}
                  >
                    <div
                      aria-hidden="true"
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: 110,
                        height: 110,
                        borderRadius: '50%',
                        border: `1px solid ${el.edge}`,
                        boxShadow: `0 0 12px ${el.glow}`,
                        opacity: 0,
                        transform: 'translate3d(-50%, -50%, 0)',
                        animation: 'zc-elem-shock 320ms ease-out both',
                      }}
                    />
                    {Array.from({ length: sparkleCount }).map((_, i) => {
                      const ang = (i / sparkleCount) * Math.PI * 2;
                      const dx = Math.round(Math.cos(ang) * 18);
                      const dy = Math.round(Math.sin(ang) * 18);
                      return (
                        <div
                          key={`sp_${r}_${c}_${fxTick}_${i}`}
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            width: fxLow ? 5 : 6,
                            height: fxLow ? 5 : 6,
                            borderRadius: 999,
                            background: el.glow,
                            filter: 'none',
                            transform: 'translate3d(-50%, -50%, 0)',
                            opacity: 0,
                            animation: 'zc-sparkle 260ms ease-out both',
                            // custom travel
                            '--dx': `${dx}px`,
                            '--dy': `${dy}px`,
                            boxShadow: 'none',
                          }}
                        />
                      );
                    })}
                  </div>
                )}

                {matchCue && (
                  <div
                    aria-hidden="true"
                    className="zc-match-creation-cue"
                    style={{
                      position: 'absolute',
                      inset: 4,
                      borderRadius: 14,
                      pointerEvents: 'none',
                      zIndex: 11,
                      border: `1px solid ${Number(matchTier) >= 5 ? 'rgba(255,215,0,0.92)' : 'rgba(255,255,255,0.72)'}`,
                      boxShadow: Number(matchTier) >= 5
                        ? '0 0 22px rgba(255,215,0,0.75), inset 0 0 16px rgba(255,215,0,0.32)'
                        : '0 0 16px rgba(255,255,255,0.48), inset 0 0 12px rgba(255,215,0,0.18)',
                      animation: 'zc-match-cue 300ms ease-out both',
                    }}
                  />
                )}

                <TileView
                  tile={tile}
                  selected={isSel}
                  hint={false}
                  element={elementKey}
                  onClick={undefined}
                />
              </div>
            );
          })
        )}
      </div>

      {disabled && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.16)',
            backdropFilter: 'blur(1px)',
            WebkitBackdropFilter: 'blur(1px)',
            pointerEvents: 'none',
            zIndex: 50,
          }}
        />
      )}
      </div>
    </div>
  );
}
